// PII masking utilities. Applied to event payloads before dispatch.
// Allowlist: fields explicitly marked unmasked by schema (e.g. amount_cents).

const EMAIL_RE = /([a-zA-Z0-9._%+-]{1,3})[a-zA-Z0-9._%+-]*(@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
// Phone BR: require word boundary AND a recognizable prefix (+55, DDD in
// parens, or leading 9-prefix mobile). Avoids matching digit runs inside
// UUIDs (e.g. "550e8400-...-446655440000") and query strings.
const PHONE_BR_RE = /(?<![\w-])(?:\+?55[\s-]?)?(?:\(\d{2}\)|\d{2})[\s-]?9\d{4}[\s-]?\d{4}(?![\w-])/g;
const CPF_RE = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
const CARD_RE = /\b(?:\d[ -]*?){13,19}\b/g;
// PIX / UUID v4-ish — always mask globally; UUIDs in stack traces or URLs
// almost never carry semantic value for debugging and frequently carry PII.
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
// OAuth bearer tokens (Authorization-style or inline).
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
// JWT: three base64url segments separated by dots, leading with "eyJ".
const JWT_RE = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\b/g;
// Generic long opaque tokens (API keys, refresh tokens) — 32+ url-safe chars
// without spaces, with mixed case + digits to reduce false positives on hashes
// of fixed format that we mask via other rules.
const OPAQUE_TOKEN_RE = /\b(?=[A-Za-z0-9_-]{32,})(?=[^\s]*[A-Z])(?=[^\s]*[a-z])(?=[^\s]*\d)[A-Za-z0-9_-]{32,}\b/g;
// Well-known short API key prefixes (Stripe, OpenAI, GitHub, Slack, Google,
// AWS). These can be <32 chars and would slip past OPAQUE_TOKEN_RE.
const API_KEY_PREFIX_RE = /\b(?:sk|pk|rk|whsec|sk_test|sk_live|pk_test|pk_live|xox[baprs]|ghp|gho|ghu|ghs|ghr|AIza|AKIA|ASIA)[_-][A-Za-z0-9_-]{8,}/g;

// Field names that always mean financial/PII even if value passes regex.
const SENSITIVE_FIELD_PATTERNS = [
  /balance/i,
  /wallet/i,
  /pix/i,
  /receipt/i,
  /comprovante/i,
  /api[_-]?key/i,
  /secret/i,
  /token/i,
  /password/i,
  /senha/i,
  /document/i,
  /cpf/i,
  /cnpj/i,
];

export function maskString(value: string): string {
  if (!value) return value;
  let out = value;
  // ORDER MATTERS — strip the most-specific / most-greedy patterns first so
  // later rules (phone) don't mutilate inner digits of a UUID/JWT/card.
  out = out.replace(BEARER_RE, "Bearer [REDACTED]");
  out = out.replace(JWT_RE, "[REDACTED_JWT]");
  out = out.replace(UUID_RE, "[REDACTED_UUID]");
  out = out.replace(CARD_RE, (m) => `****-****-****-${m.slice(-4)}`);
  out = out.replace(CPF_RE, "***.***.***-**");
  out = out.replace(EMAIL_RE, (_m, p1, p2) => `${p1}***${p2}`);
  out = out.replace(PHONE_BR_RE, "+55 ** ****-****");
  out = out.replace(API_KEY_PREFIX_RE, "[REDACTED_APIKEY]");
  out = out.replace(OPAQUE_TOKEN_RE, "[REDACTED_TOKEN]");
  return out;
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_FIELD_PATTERNS.some((re) => re.test(key));
}

function maskValue(key: string, value: unknown, allowlist: Set<string>): unknown {
  if (allowlist.has(key)) return value;
  if (value == null) return value;

  if (typeof value === "string") {
    if (isSensitiveKey(key)) return "[REDACTED]";
    return maskString(value);

  }
  if (typeof value === "number" || typeof value === "boolean") {
    if (isSensitiveKey(key) && !allowlist.has(key)) return "[REDACTED]";
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v, i) => maskValue(`${key}[${i}]`, v, allowlist));
  }
  if (typeof value === "object") {
    return maskObject(value as Record<string, unknown>, allowlist);
  }
  return value;
}

export function maskObject(
  obj: Record<string, unknown>,
  allowlist: Set<string> = new Set(),
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = maskValue(k, v, allowlist);
  }
  return out;
}

// Truncate long URLs and strip click-id params from referrer/URL strings.
export function sanitizeUrl(url: string | null, stripParams: readonly string[], maxLen = 300): string | null {
  if (!url) return url;
  try {
    const u = new URL(url, "https://placeholder.local");
    for (const p of stripParams) u.searchParams.delete(p);
    const reconstructed = u.origin === "https://placeholder.local" ? u.pathname + u.search : u.toString();
    return reconstructed.length > maxLen ? reconstructed.slice(0, maxLen) + "…" : reconstructed;
  } catch {
    return url.length > maxLen ? url.slice(0, maxLen) + "…" : url;
  }
}
